import React, { useEffect, useMemo, useState } from "react";
import DonutChart from "../../components/dashboard/donut-chart";
import "./home.scss";

import HomeAppointmentList from "../../components/dashboard/home-appointment-list";
import AssistantTodayConsole from "../../components/dashboard/assistant-today-console";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "antd";
import {
  getAllAppointmentCount,
  getBarChartAppointment,
  getClinicStats,
  getOrganizationInsights,
} from "../../store/slices/dashboardSlice";
import BarChart from "../../components/dashboard/bar-chart";
import {
  DashboardBarChartSkeleton,
  DashboardOverviewSkeleton,
  DashboardStatCardsSkeleton,
} from "../../components/dashboard/dashboard-skeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faCalendarDays,
  faChartColumn,
  faChevronRight,
  faClipboardCheck,
  faDroplet,
  faFlask,
  faMars,
  faPills,
  faUserGroup,
  faVenus,
} from "@fortawesome/free-solid-svg-icons";
import { formatOfficeLocationLine } from "../../utils/common-funtions/common";

function formatInputDate(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const Home = () => {
  const dispatch = useDispatch();
  const [barChartData, setBarChartData] = useState();
  const [donutChartData, setDonutChartData] = useState();
  const today = formatInputDate(new Date());
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  let {
    allAppointmentsCount,
    barChartAppointment,
    clinicStats,
    organizationInsights,
    isBarChartLoading,
    isOperationLoading,
    isOrgInsightsLoading,
  } = useSelector((state) => state.dashboard);
  const { selectedRole, user } = useSelector((state) => state.auth);
  const userType = user?.user?.type;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (userType === "assistant") return;
    if (selectedRole?._id) {
      dispatch(getBarChartAppointment(selectedRole._id));
      dispatch(getAllAppointmentCount(selectedRole._id));
    }
  }, [dispatch, selectedRole?._id, userType]);

  useEffect(() => {
    if (userType === "assistant") return;
    if (!selectedRole?._id) return;
    dispatch(
      getClinicStats({
        userRoleId: selectedRole._id,
        startDate,
        endDate,
      })
    );
  }, [dispatch, selectedRole?._id]);

  useEffect(() => {
    if (userType === "assistant") return;
    if (!selectedRole?._id) return;
    dispatch(getOrganizationInsights(selectedRole._id));
  }, [dispatch, selectedRole?._id]);

  useEffect(() => {
    if (barChartAppointment) {
      const dates = Object.keys(barChartAppointment);
      const values = Object.values(barChartAppointment);
      setBarChartData({ dates, values });
    }
    if (allAppointmentsCount) {
      const status = Object.keys(allAppointmentsCount);
      const values = Object.values(allAppointmentsCount);
      setDonutChartData({ status, values });
    }
  }, [allAppointmentsCount, barChartAppointment]);

  useEffect(() => {
    const userAgent = navigator.userAgent;
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    setIsMobile(mobileRegex.test(userAgent));
  }, []);

  const orgAgeGroups = organizationInsights?.ageGroups ?? [];
  const orgAgeMax = Math.max(1, ...orgAgeGroups.map((g) => g.count));
  const orgBloodRows = organizationInsights?.bloodGroupByCount ?? [];
  const orgBloodMax = Math.max(1, ...orgBloodRows.map((g) => g.count));

  const overviewStatItems = useMemo(() => {
    if (!organizationInsights) return [];
    return [
      {
        key: "locations",
        label: "My Chambers",
        value: organizationInsights.totalLocations ?? "—",
        hint: "Locations where you have an active role",
        icon: faBuilding,
        variant: "ocean",
      },
      {
        key: "patients",
        label: "My Patients",
        value: organizationInsights.totalPatients ?? "—",
        hint: "Patients linked to you across all chambers",
        icon: faUserGroup,
        variant: "violet",
      },
      {
        key: "appointments",
        label: "Total Visits",
        value: organizationInsights.totalAppointments ?? "—",
        hint: "All appointments booked for you (all time)",
        icon: faCalendarDays,
        variant: "teal",
      },
      {
        key: "orders",
        label: "Total prescribed",
        hint: "Medicine + lab test lines across all visits",
        icon: faPills,
        variant: "amber",
        compound: [
          { label: "Medicines", value: organizationInsights.totalMedicinesPrescribed ?? "—" },
          { label: "Lab tests", value: organizationInsights.totalLabTestsPrescribed ?? "—" },
        ],
      },
    ];
  }, [organizationInsights]);

  const donutHasData = useMemo(
    () => Array.isArray(donutChartData?.values) && donutChartData.values.some((v) => Number(v) > 0),
    [donutChartData]
  );

  const [overviewOpen, setOverviewOpen] = useState(true);

  const statItems = [
    {
      label: "Appointments",
      value: clinicStats?.totalAppointments ?? "—",
      hint: "Booked for you at this chamber, by appointment date",
      icon: faCalendarDays,
      variant: "teal",
    },
    {
      label: "Visits done",
      value: clinicStats?.visitedAppointments ?? "—",
      hint: "Appointments where a visit was started",
      icon: faClipboardCheck,
      variant: "ocean",
    },
    {
      label: "Male patients",
      value: clinicStats?.malePatientCount ?? "—",
      hint: "Distinct male patients on those appointments",
      icon: faMars,
      variant: "indigo",
    },
    {
      label: "Female patients",
      value: clinicStats?.femalePatientCount ?? "—",
      hint: "Distinct female patients on those appointments",
      icon: faVenus,
      variant: "rose",
    },
    {
      label: "Medicines",
      value: clinicStats?.totalMedicinesPrescribed ?? "—",
      hint: "Prescription lines on visits in range",
      icon: faPills,
      variant: "amber",
    },
    {
      label: "Lab tests",
      value: clinicStats?.totalLabTestsPrescribed ?? "—",
      hint: "Lab order lines on visits in range",
      icon: faFlask,
      variant: "violet",
    },
  ];

  if (!isMobile && userType === "assistant") {
    return (
      <section
        className="home-dashboard content-wrapper main-content home-section mt-0 pt-0"
        style={{ marginLeft: "69px" }}
      >
        <div className="home-dashboard__shell">
          <AssistantTodayConsole />
        </div>
      </section>
    );
  }

  return (
    <section
      className={`home-dashboard ${!isMobile ? "content-wrapper main-content home-section mt-0 pt-0" : ""}`}
      style={{ marginLeft: !isMobile ? "69px" : "" }}
    >
      {!isMobile && (
        <div className="home-dashboard__shell">
          {/* Single-row header: title · chamber · date range · apply */}
          <div className="home-dashboard__header">
            <div className="home-dashboard__header-left">
              <h1 className="home-dashboard__title">Dashboard</h1>
              {selectedRole?.location && (
                <span className="home-dashboard__chamber-pill" title={formatOfficeLocationLine(selectedRole.location)}>
                  <strong>{selectedRole.location.name || selectedRole.location.alias || "—"}</strong>
                  {formatOfficeLocationLine(selectedRole.location) && (
                    <span className="home-dashboard__chamber-pill-addr">
                      {" · "}
                      {formatOfficeLocationLine(selectedRole.location)}
                    </span>
                  )}
                </span>
              )}
            </div>

            <div className="home-dashboard__header-right">
              <div className="home-dashboard__filter-inline" role="group" aria-label="Dashboard date range">
                <label className="home-filter-field__label" htmlFor="home-date-from">From</label>
                <input
                  id="home-date-from"
                  type="date"
                  className="form-control home-filter-field__input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <label className="home-filter-field__label" htmlFor="home-date-to">To</label>
                <input
                  id="home-date-to"
                  type="date"
                  className="form-control home-filter-field__input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <button
                  type="button"
                  className="btn home-dashboard__filter-btn"
                  onClick={() => {
                    if (!selectedRole?._id) return;
                    dispatch(
                      getClinicStats({
                        userRoleId: selectedRole._id,
                        startDate,
                        endDate,
                      })
                    );
                  }}
                  disabled={isOperationLoading || !selectedRole?._id}
                >
                  {isOperationLoading ? (
                    <>
                      <span className="home-dashboard__filter-spinner" aria-hidden="true" />
                      Updating…
                    </>
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>
              {clinicStats?.startDate && (
                <span className="home-dashboard__range-chip" aria-label="Date range applied to KPIs">
                  Showing{" "}
                  <strong>
                    {clinicStats.startDate}
                    {clinicStats.endDate !== clinicStats.startDate ? ` → ${clinicStats.endDate}` : ""}
                  </strong>
                </span>
              )}
            </div>
          </div>

          {/* KPI tiles — date-filtered clinic stats */}
          {isOperationLoading ? (
            <DashboardStatCardsSkeleton count={6} />
          ) : (
            <div className="row g-3 home-stat-grid">
              {statItems.map((item) => (
                <div className="col-xl-2 col-lg-4 col-md-6" key={item.label}>
                  <Tooltip title={item.hint} placement="top">
                    <div className={`home-stat-card home-stat-card--${item.variant} h-100`}>
                      <span className="home-stat-card__icon-wrap" aria-hidden="true">
                        <FontAwesomeIcon icon={item.icon} className="home-stat-card__icon" />
                      </span>
                      <div className="home-stat-card__body">
                        <span className="home-stat-card__label">{item.label}</span>
                        <span className="home-stat-card__value">{item.value}</span>
                      </div>
                    </div>
                  </Tooltip>
                </div>
              ))}
            </div>
          )}

          {/* Bar chart + donut, side by side */}
          <div className="row g-3 home-charts-row home-charts-row--split align-items-stretch home-dashboard__section-gap">
            <div className="col-lg-8">
              <div className="home-panel h-100">
                <div className="home-panel__head home-panel__head--row">
                  <h2 className="home-panel__title">Daily appointments</h2>
                  <span className="home-panel__desc">This month</span>
                </div>
                <div
                  className="home-panel__body home-panel__body--chart-wide"
                  aria-busy={isBarChartLoading}
                >
                  {isBarChartLoading ? <DashboardBarChartSkeleton /> : <BarChart barChartData={barChartData} />}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="home-panel h-100">
                <div className="home-panel__head home-panel__head--row">
                  <h2 className="home-panel__title">Appointment status</h2>
                  <span className="home-panel__desc">All time</span>
                </div>
                <div className="home-panel__body home-panel__body--chart-wide home-panel__body--donut">
                  {donutHasData ? (
                    <DonutChart barChartData={donutChartData} />
                  ) : (
                    <p className="home-panel__empty mb-0">No appointment data yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent appointments */}
          <div className="home-panel home-panel--table home-dashboard__section-gap">
            <div className="home-panel__head home-panel__head--row">
              <h2 className="home-panel__title mb-0">Recent appointments</h2>
            </div>
            <div className="home-panel__body home-panel__body--flush">
              <HomeAppointmentList />
            </div>
          </div>

          {/* Practice overview — collapsible, default open */}
          <div className="home-dashboard__overview home-dashboard__section-gap">
            <button
              type="button"
              className="home-overview-toggle"
              aria-expanded={overviewOpen}
              aria-controls="home-overview-body"
              onClick={() => setOverviewOpen((v) => !v)}
            >
              <span className={`home-overview-toggle__chev${overviewOpen ? " is-open" : ""}`} aria-hidden="true">
                <FontAwesomeIcon icon={faChevronRight} />
              </span>
              <span className="home-overview-toggle__title">Practice overview</span>
              <span className="home-overview-toggle__desc">
                Across all your chambers · all time (not affected by date range)
              </span>
            </button>

            {overviewOpen && (
              <div id="home-overview-body" className="home-dashboard__overview-body">
                {isOrgInsightsLoading && (
                  <div aria-busy="true">
                    <DashboardOverviewSkeleton />
                  </div>
                )}
                {!isOrgInsightsLoading && organizationInsights && (
                  <>
                    <div className="row g-3 home-stat-grid home-org-insights__summary">
                      {overviewStatItems.map((item) => (
                        <div className="col-xl-3 col-lg-6 col-md-6" key={item.key}>
                          <Tooltip title={item.hint} placement="top">
                            <div
                              className={`home-stat-card home-stat-card--${item.variant}${
                                item.compound ? " home-stat-card--compound" : ""
                              } h-100`}
                            >
                              <span className="home-stat-card__icon-wrap" aria-hidden="true">
                                <FontAwesomeIcon icon={item.icon} className="home-stat-card__icon" />
                              </span>
                              <div className="home-stat-card__body">
                                <span className="home-stat-card__label">{item.label}</span>
                                {!item.compound && (
                                  <span className="home-stat-card__value">{item.value}</span>
                                )}
                                {item.compound && (
                                  <div className="home-stat-card__split">
                                    {item.compound.map((part) => (
                                      <div key={part.label} className="home-stat-card__split-item">
                                        <small>{part.label}</small>
                                        <b>{part.value}</b>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </Tooltip>
                        </div>
                      ))}
                    </div>

                    <div className="row g-3 home-org-insights__tables home-dashboard__overview-viz">
                      <div className="col-lg-6">
                        <div className="home-org-viz home-org-viz--age">
                          <div className="home-org-viz__head">
                            <span className="home-org-viz__head-icon" aria-hidden="true">
                              <FontAwesomeIcon icon={faChartColumn} />
                            </span>
                            <div>
                              <h3 className="home-org-viz__title">Age groups</h3>
                              <p className="home-org-viz__subtitle">Years (from date of birth)</p>
                            </div>
                          </div>
                          <ul className="home-org-viz__list" aria-label="Patients by age group">
                            {orgAgeGroups.map((row) => {
                              const pct = orgAgeMax > 0 ? Math.round((row.count / orgAgeMax) * 100) : 0;
                              return (
                                <li key={row.label} className="home-org-viz__row">
                                  <span className="home-org-viz__label">{row.label}</span>
                                  <div className="home-org-viz__bar-wrap">
                                    <div
                                      className="home-org-viz__bar home-org-viz__bar--age"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                  <span className="home-org-viz__count">{row.count}</span>
                                </li>
                              );
                            })}
                          </ul>
                          {(organizationInsights.ageOtherCount ?? 0) > 0 && (
                            <p className="home-org-viz__footnote mb-0">
                              <strong>Other:</strong> {organizationInsights.ageOtherCount} — missing DOB, future
                              DOB, or age 100+.
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="home-org-viz home-org-viz--blood">
                          <div className="home-org-viz__head">
                            <span
                              className="home-org-viz__head-icon home-org-viz__head-icon--blood"
                              aria-hidden="true"
                            >
                              <FontAwesomeIcon icon={faDroplet} />
                            </span>
                            <div>
                              <h3 className="home-org-viz__title">Blood group</h3>
                              <p className="home-org-viz__subtitle">Recorded on patient profile</p>
                            </div>
                          </div>
                          {orgBloodRows.length === 0 ? (
                            <p className="home-org-viz__empty mb-0">No blood group data yet.</p>
                          ) : (
                            <ul className="home-org-viz__list" aria-label="Patients by blood group">
                              {orgBloodRows.map((row) => {
                                const pct = orgBloodMax > 0 ? Math.round((row.count / orgBloodMax) * 100) : 0;
                                return (
                                  <li key={String(row.bloodGroup)} className="home-org-viz__row">
                                    <span className="home-org-viz__label home-org-viz__label--blood">
                                      {row.bloodGroup}
                                    </span>
                                    <div className="home-org-viz__bar-wrap">
                                      <div
                                        className="home-org-viz__bar home-org-viz__bar--blood"
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                    <span className="home-org-viz__count">{row.count}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {isMobile && (
        <div className="mobile-home">
          <div className="mobile-home__card">
            <div className="mobile-home__greeting">
              Welcome back
            </div>
            {selectedRole?.location && (
              <p className="mobile-home__location">
                {selectedRole.location.name || selectedRole.location.alias || ""}
                {selectedRole.location.organization?.name
                  ? ` · ${selectedRole.location.organization.name}`
                  : ""}
              </p>
            )}
            <p className="mobile-home__hint">
              This app is optimised for desktop. On mobile, use the{" "}
              <strong>Files</strong> button above to upload patient files.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Home;
